import React, { useState } from 'react';
import { navigate } from 'gatsby';
import { Stack, Drawer } from '@mui/material';

import useShoppingCartContext from '../../context/ShoppingCart/use-shopping-cart-context';

import ShoppingCartFooter from './ShoppingCartFooter';
import ShoppingCartHeader from './ShoppingCartHeader';
import ConfirmDestinationModal from '../ConfirmDestinationModal';
import ShoppingCartTicketsSummary from './ShoppingCartTicketsSummary';

import SignUpModal from '../../features/SignUp/components/SignUpModal';
import SignUpBenefitsModal from '../../features/SignUp/components/SignUpBenefitsModal';
import SignUpSuccessfullyModal from '../../features/SignUp/components/SignUpSuccessfullyModal';
import ShoppingCartAddServices from './ShoppingCartAddServices';
import { TravelType } from '../../domain/travels';
import { SignupFailedModal } from '../../features/SignUp/components/signup-failed-modal';

export default function ShoppingCart({ firebase, user, uid }) {
  const { isOpen, closeShoppingCart, openShoppingCartAddServices, setOpenShoppingCartAddServices, cart } =
    useShoppingCartContext();
  const [openConfirmDestinationModal, setOpenConfirmDestinationModal] = useState(false);
  const [openSignUpBenefitsModal, setOpenSignUpBenefitsModal] = useState(false);
  const [openSignUpModal, setOpenSignUpModal] = useState(false);
  const [openSignUpSuccessfullyModal, setOpenSignUpSuccessfullyModal] = useState(false);
  const [openSignupFailedModal, setOpenSignupFailedModal] = useState(false);
  const [serviceToAdd, setServiceToAdd] = useState('');
  const [originDestination, setOriginDestination] = useState({
    origin: 'México',
    destination: 'USA',
  });

  const onVerifyDestination = () => {
    if (!uid) {
      setOpenSignUpBenefitsModal(true);
    } else {
      closeShoppingCart();
      navigate('/checkout');
    }
    setOpenConfirmDestinationModal(false);
  };
  const onContinueAsGuestClick = () => {
    closeShoppingCart();
    setOpenSignUpBenefitsModal(false);
    navigate('/checkout');
  };
  const onRegisterClick = () => {
    setOpenSignUpBenefitsModal(false);
    setOpenSignUpModal(true);
  };

  const onSignUpSuccess = () => {
    setOpenSignUpModal(false);
    setOpenSignUpSuccessfullyModal(true);
    closeShoppingCart();
    setTimeout(() => {
      navigate('/checkout');
    }, 3000);
  };

  const handleSignupFailure = (error) => {
    if (error?.code === 'auth/email-already-in-use') {
      setOpenSignupFailedModal(true);
    }
  };

  const handleContinueShopping = () => {
    // If we already are in checkout, then only close shoppingCart
    if (typeof window !== 'undefined' && window.location.pathname.includes('/checkout')) {
      closeShoppingCart();
      return;
    }

    // Else, begin checkDestination flow for modals
    checkDestination();
  };

  const checkDestination = () => {
    const tickets = cart.items.filter((item) => {
      return item.type.toLowerCase().includes('ticket') && item.item.tripType === TravelType.OneWay;
    });

    const destinations = {};
    tickets.forEach((ticket) => {
      destinations[ticket.item.destination] = ticket.item.origin;
    });
    if (Object.keys(destinations).length != 1) {
      onVerifyDestination();
      return;
    }

    const destination = Object.keys(destinations)[0];
    setOriginDestination({
      origin: destinations[destination],
      destination,
    });
    setOpenConfirmDestinationModal(true);
  };

  return (
    <>
      <Drawer
        open={isOpen}
        anchor="right"
        onClose={closeShoppingCart}
        PaperProps={{
          style: {
            boxShadow: '-17px 24px 16px 0px rgba(0, 0, 0, 0.50)',
            borderRadius: 0,
          },
        }}
      >
        <Stack
          sx={{
            width: {
              xs: '100vw',
              sm: '400px',
              md: '444px',
            },
            height: '100%',
            overflowY: 'scroll',
            marginBottom: '195px',
          }}
          role="presentation"
          direction="column"
          justifyContent="space-between"
          position="relative"
        >
          <ShoppingCartHeader onCloseClick={() => closeShoppingCart} />
          <ShoppingCartTicketsSummary
            onAddTicket={(id) => {
              if (id) {
                const ticket = cart.items.find((item) => item.item.id === id);
                if (!ticket) {
                  setServiceToAdd('tickets');
                } else {
                  if (ticket.item.type === 'ticket') setServiceToAdd('tickets');
                  if (ticket.item.type === 'parking') setServiceToAdd('parking_service');
                  if (ticket.item.type === 'shuttle') setServiceToAdd('shuttle_service');
                  if (ticket.item.type === 'wheels') setServiceToAdd('wheels');
                  if (ticket.item.type === 'black-cars') setServiceToAdd('black-cars');
                }
              }
              setOpenShoppingCartAddServices(true);
            }}
          />
        </Stack>
        <ShoppingCartFooter handleAddToCartClick={handleContinueShopping} />
      </Drawer>
      <ConfirmDestinationModal
        open={openConfirmDestinationModal}
        handleClose={() => setOpenConfirmDestinationModal(false)}
        onVerifyDestination={() => onVerifyDestination()}
        selectedOrigin={originDestination.origin}
        selectedDestination={originDestination.destination}
      />
      <SignUpBenefitsModal
        open={openSignUpBenefitsModal}
        handleClose={() => setOpenSignUpBenefitsModal(false)}
        onContinueAsGuestClick={onContinueAsGuestClick}
        onRegisterClick={onRegisterClick}
      />
      <SignUpModal
        firebase={firebase}
        user={user}
        uid={uid}
        open={openSignUpModal}
        handleClose={() => setOpenSignUpModal(false)}
        onSignUpSuccess={onSignUpSuccess}
        onSignupFailure={handleSignupFailure}
      />
      <SignUpSuccessfullyModal
        open={openSignUpSuccessfullyModal}
        handleClose={() => setOpenSignUpSuccessfullyModal(false)}
      />
      <SignupFailedModal isOpen={openSignupFailedModal} onClose={() => setOpenSignupFailedModal(false)} />
      <ShoppingCartAddServices
        open={openShoppingCartAddServices}
        service={serviceToAdd}
        handleClose={() => setOpenShoppingCartAddServices(false)}
      />
    </>
  );
}
